// See: https://cimpress-support.atlassian.net/wiki/spaces/CI/pages/10267623744/Units+and+Measurements+CDIF

export const LENGTH_REGEX =
    /^-?([0-9]*(\.[0-9]+)?(?:e[-+]?\d+)?)([tT][wW][iI][pP]|[pP][xX]|[pP][tT]|[mM][mM]|[cC][mM]|[dD][mM]|[mM]|[vV][pP][xX]|[eE][mM]|[iI][nN])$/;

export const LENGTH_WITHOUT_UNIT_REGEX = /^-?\d+$/;

// TODO - Need to investigate and fix below vulnerable regex
const LENGTH_UNIT = /[^\d]+$/;

export enum LengthConvertibleUnit {
    Millimeter = 'mm',
    Decimeter = 'dm',
    Centimeter = 'cm',
    Meter = 'm',
    Inch = 'in',
    Twip = 'twip',
    Point = 'pt',
    VistaPixel = 'vpx',
    Pixel = 'px',
}

export enum LengthNonConvertibleUnit {
    Em = 'em',
}

type LengthUnit = LengthConvertibleUnit | LengthNonConvertibleUnit;

export class Length {
    readonly value: number;
    readonly unit: LengthUnit;

    constructor(value: number, unit: LengthUnit) {
        this.value = value;
        this.unit = unit;
    }

    // Constructors

    static create(value: number, unit: LengthUnit) {
        return new Length(value, unit);
    }

    static millimeters(value: number) {
        return new Length(value, LengthConvertibleUnit.Millimeter);
    }

    static centimeters(value: number) {
        return new Length(value, LengthConvertibleUnit.Centimeter);
    }

    static decimeters(value: number) {
        return new Length(value, LengthConvertibleUnit.Decimeter);
    }

    static meters(value: number) {
        return new Length(value, LengthConvertibleUnit.Meter);
    }

    static inches(value: number) {
        return new Length(value, LengthConvertibleUnit.Inch);
    }

    static points(value: number) {
        return new Length(value, LengthConvertibleUnit.Point);
    }

    static em(value: number) {
        return new Length(value, LengthNonConvertibleUnit.Em);
    }

    static pixels(value: number) {
        return new Length(value, LengthConvertibleUnit.Pixel);
    }

    static toMillimetersValue(value: string) {
        return Length.parse(value).toMillimeters().value;
    }

    // Conversions

    toUnit(unit: LengthUnit) {
        return convert(this.value, this.unit, unit);
    }

    toMillimeters() {
        return this.toUnit(LengthConvertibleUnit.Millimeter);
    }

    toCentimeters() {
        return this.toUnit(LengthConvertibleUnit.Centimeter);
    }

    toDecimeters() {
        return this.toUnit(LengthConvertibleUnit.Decimeter);
    }

    toMeters() {
        return this.toUnit(LengthConvertibleUnit.Meter);
    }

    toInches() {
        return this.toUnit(LengthConvertibleUnit.Inch);
    }

    toPoints() {
        return this.toUnit(LengthConvertibleUnit.Point);
    }

    toPixel() {
        return this.toUnit(LengthConvertibleUnit.Pixel);
    }

    // Parsing

    /**
     * @throws
     */
    static parse(lengthStr: string, fallback?: Length): Length {
        if (!lengthStr.match(LENGTH_REGEX)) {
            if (fallback) return fallback;
            else throw new Error(`Cannot convert ${lengthStr} to Length`);
        }

        const unit = lengthStr.match(LENGTH_UNIT)![0];
        const valueString = lengthStr.substring(0, lengthStr.length - unit.length);

        const parsedValue = finiteNumber(parseFloat(valueString));

        const parsedUnit = parseLengthUnit(unit);

        if (!parsedUnit) {
            throw new Error(`Invalid unit, expected one of: ${VALID_UNITS.join(', ')}, got: ${unit}`);
        }

        return new Length(parsedValue, parsedUnit);
    }

    // Math operations

    add(rightOperand: Length): Length {
        const newValue = this.value + rightOperand.toUnit(this.unit).value;
        return new Length(newValue, this.unit);
    }

    addValue(value: number): Length {
        return new Length(this.value + value, this.unit);
    }

    subtract(rightOperand: Length): Length {
        const newValue = this.value - rightOperand.toUnit(this.unit).value;
        return new Length(newValue, this.unit);
    }

    subtractValue(value: number): Length {
        return new Length(this.value - value, this.unit);
    }

    multiply(rightOperand: Length): Length {
        const newValue = this.value * rightOperand.toUnit(this.unit).value;
        return new Length(newValue, this.unit);
    }

    multiplyBy(value: number): Length {
        return new Length(this.value * value, this.unit);
    }

    divide(rightOperand: Length): Length {
        const newValue = this.value / rightOperand.toUnit(this.unit).value;
        return new Length(newValue, this.unit);
    }

    divideBy(value: number): Length {
        return new Length(this.value / value, this.unit);
    }

    // Comparison

    equals(rightOperand: Length): boolean {
        return this.value === rightOperand.value && this.unit === rightOperand.unit;
    }

    lessThanOrEqual(rightOperand: Length): boolean {
        const convertedRightOperand = rightOperand.toUnit(this.unit);
        return this.value <= convertedRightOperand.value;
    }

    greaterThanOrEqual(rightOperand: Length): boolean {
        const convertedRightOperand = rightOperand.toUnit(this.unit);
        return this.value >= convertedRightOperand.value;
    }

    lessThan(rightOperand: Length): boolean {
        const convertedRightOperand = rightOperand.toUnit(this.unit);
        return this.value < convertedRightOperand.value;
    }

    greaterThan(rightOperand: Length): boolean {
        const convertedRightOperand = rightOperand.toUnit(this.unit);
        return this.value > convertedRightOperand.value;
    }

    toString(): string {
        return `${this.value}${this.unit}`;
    }
}

function finiteNumber(value: number): number {
    if (!Number.isFinite(value)) {
        throw new Error(`Value must be a finite number, got: ${value}`);
    }

    return value;
}

const VALID_UNITS: LengthUnit[] = [...Object.values(LengthConvertibleUnit), ...Object.values(LengthNonConvertibleUnit)];

const VALID_CONVERTIBLE_UNITS = Object.values(LengthConvertibleUnit);

function parseLengthUnit(unitStr: string): LengthUnit | undefined {
    return VALID_UNITS.find((unit) => unit === (unitStr as LengthUnit));
}

function parseConvertibleLengthUnit(unitStr: LengthUnit): LengthConvertibleUnit | undefined {
    return VALID_CONVERTIBLE_UNITS.find((unit) => unit === unitStr);
}

/** Source units to millimeters */
const LENGTH_UNIT_CONVERSION_TABLE = {
    [LengthConvertibleUnit.Meter]: 1000,
    [LengthConvertibleUnit.Decimeter]: 100,
    [LengthConvertibleUnit.Centimeter]: 10,
    [LengthConvertibleUnit.Millimeter]: 1,
    [LengthConvertibleUnit.Inch]: 25.4,
    [LengthConvertibleUnit.Point]: 0.35278,
    [LengthConvertibleUnit.Twip]: 0.01764,
    [LengthConvertibleUnit.VistaPixel]: 0.176389,
    [LengthConvertibleUnit.Pixel]: 0.264583333, // for 96 DPI
} satisfies Record<LengthConvertibleUnit, number>;

function convert(value: number, sourceUnit: LengthUnit, targetUnit: LengthUnit): Length {
    const sourceUnitParsed = parseConvertibleLengthUnit(sourceUnit);
    const targetUnitParsed = parseConvertibleLengthUnit(targetUnit);

    if (!sourceUnitParsed || !targetUnitParsed) {
        throw new Error(
            `Invalid unit to convert, expected two of: ${Object.values(LengthConvertibleUnit).join(
                ', ',
            )}, got: ${sourceUnit} as source unit and ${targetUnit} as target unit`,
        );
    }

    const sourceRatio = LENGTH_UNIT_CONVERSION_TABLE[sourceUnitParsed];
    const targetRatio = LENGTH_UNIT_CONVERSION_TABLE[targetUnitParsed];

    return Length.create((value * sourceRatio) / targetRatio, targetUnitParsed);
}
